import { useEffect, useRef } from 'react';
import type { InboundAs2DocumentDetails, InboundAs2DocumentSummary } from '../types/api';
import { ErrorState, LoadingState } from './RequestState';
import { PayloadViewer } from './PayloadViewer';

interface InboundPayloadDialogProps {
  document: InboundAs2DocumentSummary | null;
  details: InboundAs2DocumentDetails | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
  onRetry: () => void;
}

export function InboundPayloadDialog({
  document: message,
  details,
  isLoading,
  error,
  onClose,
  onRetry,
}: InboundPayloadDialogProps) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!message) return undefined;

    const previousFocus = documentGlobalActiveElement();
    dialogRef.current?.querySelector<HTMLElement>('button')?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = readFocusableElements(dialogRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && window.document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && window.document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    window.document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [message]);

  if (!message) return null;

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="modal-dialog payload-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inbound-payload-title"
      >
        <div className="panel-header">
          <div>
            <h2 id="inbound-payload-title">Raw payload</h2>
            <p>{message.documentType} document from {message.partnerCode} · {message.contentType}</p>
          </div>
          <button type="button" className="ghost-button" onClick={onClose} aria-label="Close payload">
            Close
          </button>
        </div>
        <div aria-busy={isLoading}>
          {isLoading ? <LoadingState label="Loading payload…" /> : null}
          {error ? <ErrorState message={error} onRetry={onRetry} /> : null}
          {!isLoading && !error && details ? (
            <PayloadViewer
              value={details.payload}
              contentType={details.contentType}
              filename={`${details.id}.payload.txt`}
              className="code-block content-viewer raw-payload-viewer"
            />
          ) : null}
        </div>
      </section>
    </div>
  );
}

function documentGlobalActiveElement(): HTMLElement | null {
  return window.document.activeElement instanceof HTMLElement ? window.document.activeElement : null;
}

function readFocusableElements(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(
    'button:not(:disabled), [href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])',
  ));
}
